import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select, Input, Button, Icon, Space, DatePicker } from 'udesk-ui';
import moment from 'moment';

export const NewAssociateComponent = React.memo((props) => {
    const {
        associateList,
        locales,
        value,
        onChange,
        showTip = true,
        disabled = false,
        isTicketField,
        isCustomerField,
        scope,
        onScopeChange,
        isTicket,
    } = props;

    const [hasCustomField, setHasCustomField] = useState(false);
    const [hasCustomValue, setHasCustomValue] = useState(false);
    const [field, setField] = useState('');
    const [operator, setOperator] = useState('');
    const [customField, setCustomField] = useState('');

    const operatorMap = useMemo(
        () =>
            new Map([
                [
                    /^Is\((.+)\)/,
                    {
                        format: 'Is({0}, {1})',
                        operatorValue: 'Is',
                        operatorLabel: locales.enums.fieldOperators.is,
                    },
                ],
                [
                    /^not Is\((.+)\)/,
                    {
                        format: 'not Is({0}, {1})',
                        operatorValue: 'not Is',
                        operatorLabel: locales.enums.fieldOperators.not,
                    },
                ],
                [
                    /(\S*) > (\S*.*)/,
                    {
                        format: '{0} > {1}',
                        operatorValue: '>',
                        operatorLabel: locales.enums.fieldOperators.greaterThan,
                    },
                ],
                [
                    /(\S*) < (\S*.*)/,
                    {
                        format: '{0} < {1}',
                        operatorValue: '<',
                        operatorLabel: locales.enums.fieldOperators.lessThan,
                    },
                ],
                [
                    /^Keyword\((.+)\)/,
                    {
                        format: 'Keyword({0}, {1})',
                        operatorValue: 'Keyword',
                        operatorLabel: locales.enums.fieldOperators.contains,
                    },
                ],
                [
                    /^IsEmpty\((.+)\)/,
                    {
                        format: 'IsEmpty({0})',
                        operatorValue: 'IsEmpty',
                        operatorLabel: locales.enums.fieldOperators.isNull,
                    },
                ],
                [
                    /^not IsEmpty\((.+)\)/,
                    {
                        format: 'not IsEmpty({0})',
                        operatorValue: 'not IsEmpty',
                        operatorLabel: locales.enums.fieldOperators.isNotNull,
                    },
                ],
            ]),
        [
            locales.enums.fieldOperators.contains,
            locales.enums.fieldOperators.greaterThan,
            locales.enums.fieldOperators.is,
            locales.enums.fieldOperators.isNotNull,
            locales.enums.fieldOperators.isNull,
            locales.enums.fieldOperators.lessThan,
            locales.enums.fieldOperators.not,
        ]
    );

    const getOperator = useCallback(
        (value) => {
            const operators = [...operatorMap].filter(([key]) => key.test(value));
            const operator = operators[operators.length - 1];
            return operator;
        },
        [operatorMap]
    );

    const transformCustomValue = (value) => {
        const operator = getOperator(value);

        if (Array.isArray(operator) && operator.length) {
            const matches = Array.isArray(value.match(operator[0]))
                ? [...value.match(operator[0])].slice(1)
                : [];

            let [field, ...customValue] = matches;

            if (['Is', 'Keyword', 'not Is'].includes(operator[1].operatorValue)) {
                let values = field.split(',').map((i) => i.trim());
                customValue = values.slice(1);
            }

            if (Array.isArray(customValue)) {
                customValue = customValue.map((i) => i.trim()).join(',');
            }

            return customValue;
        } else {
            return '';
        }
    };

    const customValue = transformCustomValue(value);

    const selectOptions = useMemo(() => {
        const result = associateList
            .filter((item) => item.fieldList?.length > 0)
            .map((item, index) => {
                let label = '';
                if (item.type === 1) {
                    label = locales.components.operatorListGather.associateData;
                }
                if (item.type === 3) {
                    label = locales.components.operatorListGather.pretreatmentData;
                }
                if (item.type === 6) {
                    if (isTicket) {
                        label =
                            /* 自定义字段 */ UdeskLocales['current'].components
                                .checkPointOperatorListGather.associate.normal.customFields;
                    } else {
                        label = locales.components.operatorListGather.businessData;
                    }
                }
                if (item.type === 18) {
                    label = locales.components.operatorListGather.associateData;
                }
                if (item.type === 15) {
                    label =
                        /* 工单信息 */ UdeskLocales['current'].components
                            .checkPointOperatorListGather.associate.normal.workOrderInformation;
                }
                if (item.type === 16) {
                    label =
                        /* 客户信息 */ UdeskLocales['current'].components
                            .checkPointOperatorListGather.associate.normal.customerInformation;
                }
                if (item.type === 24) {
                    label =
                        /* 处理时间 */ UdeskLocales['current'].components
                            .checkPointOperatorListGather.associate.normal.processingTime;
                }

                return {
                    label,
                    options: item.fieldList.map((item, indexInChildren) => {
                        return {
                            label: item.label,
                            value: item.label,
                            key: `${index}/${indexInChildren}${item.label}`,
                        };
                    }),
                };
            });
        return result;
    }, [
        associateList,
        isTicket,
        locales.components.operatorListGather.associateData,
        locales.components.operatorListGather.businessData,
        locales.components.operatorListGather.pretreatmentData,
    ]);

    const getCustomField = (associateList) => {
        let noteCustomField = null;
        const targetTypeFieldList = associateList.find((i) => i.type === 6);
        if (targetTypeFieldList && Array.isArray(targetTypeFieldList.fieldList)) {
            noteCustomField = targetTypeFieldList.fieldList.find(
                (i) => i.fieldName === 'note_custom_fields'
            );
        }
        return noteCustomField;
    };

    useEffect(() => {
        let noteCustomField = getCustomField(associateList);

        if (noteCustomField) {
            setHasCustomField((value || '').indexOf(noteCustomField.label) >= 0);
        }
        const operator = getOperator(value);

        if (Array.isArray(operator) && operator.length) {
            setHasCustomValue(
                !(
                    operator[1].operatorValue === 'IsEmpty' ||
                    operator[1].operatorValue === 'not IsEmpty'
                )
            );

            const matches = Array.isArray(value.match(operator[0]))
                ? [...value.match(operator[0])].slice(1)
                : [];

            let [field] = matches;

            if (['Is', 'Keyword', 'not Is'].includes(operator[1].operatorValue)) {
                let values = field.split(',').map((i) => i.trim());
                field = values[0];
            }

            if (field && (field.endsWith(', ') || field.endsWith(','))) {
                field = field.replace(/,\s+$/, '');
            }

            setOperator(operator[1]?.operatorValue ?? '');

            if (field) {
                setField(
                    noteCustomField && (field || '').indexOf(noteCustomField.label) >= 0
                        ? noteCustomField.label
                        : field.replace('[', '').replace(']', '')
                );
            }

            if (field) {
                setCustomField(
                    noteCustomField && (field || '').indexOf(noteCustomField.label) >= 0
                        ? field.replace(noteCustomField.label, '').replace('[', '').replace(']', '')
                        : ''
                );
            }
        } else {
            let v = (value || '').replace('[', '').replace(']', '');
            let hasCus = v.includes('-');

            if (hasCus) {
                const idx = v.indexOf('-');
                setField(v.slice(0, idx + 1));
                setCustomField(v.slice(idx + 1) || '');
            } else {
                setField(v || '');
            }

            setHasCustomValue(false);
            setOperator('');
        }
    }, [associateList, value, operatorMap, getOperator]);

    const onValuesChange = (targetKey, value) => {
        let operator = getOperator(props.value);
        let ret = props.value;
        if (operator || targetKey === 'operator') {
            if (targetKey === 'operator') {
                operator = [...operatorMap].filter(([, v]) => v.operatorValue === value)[0];
                ret = operator[1].format
                    .replace('{0}', `[${field}${customField}]`)
                    .replace('{1}', customValue);
            } else if (targetKey === '{0}') {
                // 字段变化时，重置操作符和值
                ret = [...operatorMap][0][1].format.replace('{0}', `[${value}]`).replace('{1}', '');
            } else if (targetKey === '{1}') {
                // 值处理
                ret = operator[1].format
                    .replace('{0}', `[${field}${customField}]`)
                    .replace(
                        '{1}',
                        Array.isArray(value)
                            ? value.join(',')
                            : value.target
                            ? value.target.value
                            : value
                    );
            } else if (targetKey === 'customFieldValue') {
                ret = operator[1].format
                    .replace('{0}', `[${field}${value.target.value}]`)
                    .replace('{1}', customValue);
            }
            onChange?.(ret);
        } else {
            if (targetKey === 'customFieldValue') {
                value = `${field}${value.target.value}`;
            }
            onChange?.(`[${value}]`);
        }
    };

    const [originField, setOriginField] = useState();

    // 根据当前字段找到其原始数据
    useEffect(() => {
        if (field && Array.isArray(associateList)) {
            const originField = associateList
                .reduce((pre, cur) => {
                    return [...pre, ...(cur.fieldList || [])];
                }, [])
                .find((i) => i.label === field);
            setOriginField(originField);
        } else {
            setOriginField(undefined);
        }
    }, [associateList, field]);

    // 值是枚举对字段
    const isEnum = useMemo(
        () => !!originField && originField.fieldType === Udesk.enums.fieldTypes.sigleListbox.id,
        [originField]
    );

    // 值是时间字段
    const isTime = useMemo(
        () => !!originField && originField.fieldType === Udesk.enums.fieldTypes.dateTime.id,
        [originField]
    );

    // 预处理数据与其他字段传惨不同，做区别
    const isPreProcess = useMemo(() => {
        if (field) {
            const preprocess = associateList.find((i) => i.type === 3);

            if (preprocess) {
                const list = preprocess.fieldList || [];
                return list.some((item) => item.label === field);
            } else {
                return false;
            }
        } else {
            return false;
        }
    }, [associateList, field]);

    return (
        <Space style={{ marginLeft: 8 }}>
            <Select
                style={{ width: 200 }}
                value={field}
                onChange={(v) => {
                    onValuesChange('{0}', v);
                }}
                options={selectOptions}
                showSearch
                disabled={disabled}
            />

            {hasCustomField && (
                <Input
                    defaultValue={customField}
                    onChange={onValuesChange.bind(null, 'customFieldValue')}
                    style={{ width: 200 }}
                    disabled={disabled}
                />
            )}

            {isTicketField ? (
                <Select
                    options={[
                        {
                            value: 'any',
                            label: /* 任意工单 */ UdeskLocales['current'].components
                                .checkPointOperatorListGather.associate.normal.anyWorkOrder,
                        },
                        {
                            value: 'all',
                            label: /* 全部工单 */ UdeskLocales['current'].components
                                .checkPointOperatorListGather.associate.normal.allWorkOrders,
                        },
                    ]}
                    value={scope}
                    onChange={onScopeChange}
                    style={{ width: 160 }}
                />
            ) : null}

            <Select
                value={operator}
                onChange={onValuesChange.bind(null, 'operator')}
                options={[...operatorMap.values()]
                    .filter((i) => {
                        if (isEnum) {
                            return i.operatorValue !== '>' && i.operatorValue !== '<';
                        } else {
                            return true;
                        }
                    })
                    .map((i) => ({
                        label: i.operatorLabel,
                        value: i.operatorValue,
                    }))}
                style={{ width: 100 }}
                disabled={disabled}
            ></Select>

            {hasCustomValue &&
                (isEnum ? (
                    <Select
                        options={(originField.optionValueList || []).map((o) => {
                            return {
                                label: o.name,
                                // 预处理字段传id，其他字段传name
                                value: isPreProcess ? o.id : o.name,
                            };
                        })}
                        value={customValue}
                        onChange={onValuesChange.bind(null, '{1}')}
                        style={{ minWidth: '100px' }}
                        disabled={disabled}
                    />
                ) : isTime ? (
                    <DatePicker
                        format="YYYY-MM-DD HH:mm:ss"
                        value={moment(customValue).isValid() ? moment(customValue) : undefined}
                        onChange={(v) => onValuesChange('{1}', v.format('YYYY-MM-DD HH:mm:ss'))}
                        style={{ minWidth: '100px' }}
                        disabled={disabled}
                        showTime
                    />
                ) : operator === 'Keyword' ? (
                    <Select
                        mode="tags"
                        value={customValue ? customValue.split(',') : []}
                        tokenSeparators={[',']}
                        onChange={onValuesChange.bind(null, '{1}')}
                        style={{ minWidth: '100px' }}
                        disabled={disabled}
                    />
                ) : (
                    <Input
                        value={customValue}
                        onChange={onValuesChange.bind(null, '{1}')}
                        suffix={
                            ['Is', 'not Is'].includes(operator) &&
                            !isTicketField &&
                            !isCustomerField
                                ? locales.components.operatorListGather.newAssociateComponentTip
                                : undefined
                        }
                        disabled={disabled}
                    />
                ))}

            {['Is', 'not Is'].includes(operator) &&
                showTip &&
                !isTicketField &&
                !isCustomerField && (
                    <Button
                        size="small"
                        icon={<Icon type="QuestionCircleTwoTone" antdIcon={true} />}
                        tooltip={
                            props.tip || locales.components.operatorListGather.newAssociateDataTip
                        }
                        disabled={disabled}
                    ></Button>
                )}
        </Space>
    );
});
